import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { pickAdj, SCEN_COLORS, MAX_ACTIVE } from "@/lib/scenarios";
import { KpiStrip } from "@/components/dashboard/KpiStrip";
import { RunwayChart } from "@/components/dashboard/RunwayChart";
import { RevenueTrend } from "@/components/dashboard/RevenueTrend";
import { ZeroCashCard } from "@/components/dashboard/ZeroCashCard";
import { Calibrator } from "@/components/dashboard/Calibrator";
import { PopOut } from "@/components/dashboard/PopOut";
import { ConnectionAlert } from "@/components/integrations/Health";
import { ReplaceScenarioDialog } from "@/components/dashboard/ReplaceScenarioDialog";
import { ExpenseList } from "@/components/dashboard/ExpenseList";
import { DealSlips } from "@/components/dashboard/DealSlips";
import { SourceBar } from "@/components/dashboard/SourceBar";
import { ScenarioPanel } from "@/components/dashboard/ScenarioPanel";
import { ExportButton } from "@/components/dashboard/ExportButton";
import { Skeleton } from "@/components/ui/skeleton";

const QUERY = "(min-width: 1280px)";
const useWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia(QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
};

const useScenarios = () => {
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState([]);
  const { data: saved = [] } = useQuery({ queryKey: ["scenarios"], queryFn: () => api.get("/scenarios") });
  const active = useMemo(() => [...(draft ? [{ ...draft, id: "draft" }] : []),
    ...saved.filter((s) => selected.includes(s.id))].slice(0, MAX_ACTIVE), [draft, saved, selected]);
  const adjKey = JSON.stringify(active.map(pickAdj));
  const { data: cmp } = useQuery({
    queryKey: ["compare", adjKey], enabled: active.length > 0, placeholderData: keepPreviousData,
    queryFn: () => api.post("/forecast/compare", { scenarios: active.map(pickAdj) }),
  });

  const toggle = (id) => {
    if (selected.includes(id)) return setSelected((s) => s.filter((x) => x !== id));
    if (active.length >= MAX_ACTIVE) return toast.error(`Up to ${MAX_ACTIVE} scenarios on the chart at once`);
    setSelected((s) => [...s, id]);
  };
  const [pending, setPending] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const save = async (sc, replaceId) => {
    try {
      const doc = await api.post(`/scenarios${replaceId ? `?replace_id=${replaceId}` : ""}`, pickAdj(sc));
      await qc.invalidateQueries({ queryKey: ["scenarios"] });
      if (sc === draft) setDraft(null);
      setSelected((s) => {
        const kept = s.filter((x) => x !== replaceId);
        const used = kept.length + (draft && sc !== draft ? 1 : 0);
        return used < MAX_ACTIVE ? [...kept, doc.id] : kept;
      });
      setPending(null);
      toast.success(replaceId ? `Replaced with "${doc.label}"` : `Saved "${doc.label}"`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "slots_full") return setPending(sc);
      toast.error(typeof d === "string" ? d : "Couldn't save scenario");
    }
  };
  const replace = async (id) => { setReplacing(true); await save(pending, id); setReplacing(false); };
  const remove = async (id) => {
    await api.delete(`/scenarios/${id}`);
    setSelected((s) => s.filter((x) => x !== id));
    qc.invalidateQueries({ queryKey: ["scenarios"] });
  };
  const lines = active.map((a, i) => ({ id: a.id, key: `s${i}`, label: a.label, color: SCEN_COLORS[i],
    ...(cmp?.scenarios?.[i] || a) }));
  const clear = () => { setDraft(null); setSelected([]); };
  const dialog = <ReplaceScenarioDialog pending={pending} saved={saved} busy={replacing} onReplace={replace} onCancel={() => setPending(null)} />;
  return { draft, setDraft, saved, selected, lines, cmp: active.length ? cmp : null, toggle, save, remove, clear, dialog };
};

export default function Dashboard() {
  const api = useApi();
  const qc = useQueryClient();
  const sc = useScenarios();
  const wide = useWide();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard") });
  const refresh = () => ["dashboard", "scenarios", "compare"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  if (isLoading || !data) {
    return <div className="grid gap-4"><Skeleton className="h-24 bg-[#121212]" /><Skeleton className="h-96 bg-[#121212]" /></div>;
  }
  const series = sc.cmp?.series || data.series;
  const zc = <ZeroCashCard kpis={data.kpis} impacts={sc.lines} />;
  const chart = <PopOut id="runway-chart"><RunwayChart series={series} lines={sc.lines} onClear={sc.clear} zeroCashDate={data.kpis.zero_cash_date} /></PopOut>;
  const expenses = <><DealSlips /><ExpenseList onChange={refresh} /></>;
  const panel = <ScenarioPanel {...sc} baseRunway={data.kpis.runway_months} />;
  const sim = <PopOut id="calibrator"><Calibrator onScenario={sc.setDraft} onSave={(s) => sc.save(s)} /></PopOut>;
  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <ConnectionAlert />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-[#A3A3A3]">MULTI-SYNC CALIBRATION</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Calibrated runway</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SourceBar sources={data.sources} sync={data.sync} onSynced={refresh} />
          <ExportButton />
        </div>
      </div>
      <KpiStrip kpis={data.kpis} />
      {wide ? (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4 min-w-0">{chart}{expenses}</div>
          <div className="col-span-4 space-y-4">{zc}<RevenueTrend live={data.sources?.stripe === "live"} />{panel}{sim}</div>
        </div>
      ) : (
        <div className="space-y-4">{zc}{chart}<RevenueTrend live={data.sources?.stripe === "live"} />{panel}{sim}{expenses}</div>
      )}
      {sc.dialog}
    </div>
  );
}
